import type { UserType } from '@kanbu/schema';
import { createUserSchema, updateUserSchema } from '@kanbu/schema/contracts';
import { useLingui } from '@lingui/react/macro';
import { memo } from 'react';
import { z } from 'zod';

import { Card } from '@/components/card/Card';
import { MetaFields } from '@/components/fields/MetaFields';
import { EntityCRUDForm } from '@/components/form/EntityCRUDForm';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';

import { PasswordFields } from '../../../components/fields/passwordFields/PasswordFields';
import { useUserMutation } from '../useUserMutation';

export type ProfileFormProps = {
  data: UserType;
};

export const ProfileForm = memo(function ProfileForm({
  data,
}: ProfileFormProps) {
  const { t } = useLingui();
  const {
    update: { mutateAsync: update },
    remove: { mutateAsync: remove },
  } = useUserMutation();

  const { schema: detailsSchema } = useFieldsSchema(
    [
      {
        name: 'firstName',
        label: t`First name`,
      },
      {
        name: 'lastName',
        label: t`Last name`,
      },
      {
        name: 'email',
        label: t`Email`,
        required: true,
        disabled: true,
      },
    ],
    [t],
  );

  return (
    <EntityCRUDForm
      initialData={data}
      createSchema={createUserSchema}
      updateSchema={updateUserSchema.extend({
        email: z.string().email().max(255),
        confirmPassword: z.string().max(255).optional(),
      })}
      onUpdate={async values => {
        const { confirmPassword, email, ...restValues } =
          values as unknown as UserType & {
            confirmPassword: string;
            oldPassword: string;
          };

        return update(
          updateUserSchema.parse({
            ...restValues,
            id: data.id,
          }),
        );
      }}
      // FIXME self delete is currently disabled
      onRemove={async ({ id }) => {
        await remove({ id });
      }}
      showDelete={false}
    >
      <EntityCRUDForm.Col>
        <Card title={t`Basic details`}>
          <SchemaFields schema={detailsSchema} />
        </Card>
      </EntityCRUDForm.Col>
      <EntityCRUDForm.Col>
        <PasswordFields />
      </EntityCRUDForm.Col>
      <EntityCRUDForm.Col>
        <MetaFields data={data} />
      </EntityCRUDForm.Col>
    </EntityCRUDForm>
  );
});
